/**
 * The review queue: what waits for a lawyer, and for how long.
 *
 * Oldest first, because the question this screen answers is "what is late".
 * Which cases are in review is decided by the server from the opinions — the
 * same rule that gates the delivery — so this list and the gate cannot drift.
 * For administrators it also shows where the lawyers keep correcting the
 * engine, rule by rule: that is what feeds the correction of the matrix.
 */
import { useMemo } from 'react'

import Card from '@mui/material/Card'
import CardHeader from '@mui/material/CardHeader'
import Divider from '@mui/material/Divider'
import Typography from '@mui/material/Typography'
import { createColumnHelper, getCoreRowModel, getPaginationRowModel, useReactTable } from '@tanstack/react-table'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'

import CustomChip from '@core/components/mui/chip'
import { TableDefault } from '@components/table-default'

import { useTRPC } from '@/lib/trpc'
import { formatDateTime } from '@/lib/due-diligence'

export const Route = createFileRoute('/_dashboard/reviews')({
  component: ReviewQueue
})

type QueueRow = {
  diligenceId: string
  projectName: string
  address: string
  since: Date
  waitingMs: number
  latestVersion: number
}

type DivergenceRow = {
  ruleId: string
  engineSeverity: 'red' | 'yellow'
  raised: number
  lowered: number
  cases: number
}

const HOUR = 3_600_000
const DAY = 24 * HOUR

/** "3 dias", "5 h" — the unit that makes lateness obvious. */
const waited = (ms: number) => {
  if (ms >= DAY) return `${Math.floor(ms / DAY)} dia(s)`
  if (ms >= HOUR) return `${Math.floor(ms / HOUR)} h`

  return 'menos de 1 h'
}

/** More than two days waiting is late — the color says so before anyone reads the number. */
const waitingColor = (ms: number) => (ms >= 2 * DAY ? 'error' : ms >= DAY ? 'warning' : 'secondary')

const queueColumns = createColumnHelper<QueueRow>()
const divergenceColumns = createColumnHelper<DivergenceRow>()

function ReviewQueue() {
  const trpc = useTRPC()
  const navigate = useNavigate()

  const { data: user } = useQuery(trpc.auth.me.queryOptions())
  const { data: queue = [], isPending } = useQuery(trpc.diligences.reviewQueue.queryOptions())
  const isAdmin = user?.role === 'admin'
  const { data: divergence = [] } = useQuery({ ...trpc.diligences.riskDivergence.queryOptions(), enabled: isAdmin })

  const columns = useMemo(
    () => [
      queueColumns.accessor('projectName', {
        header: 'Caso',
        cell: ({ row }) => (
          <div className='flex flex-col'>
            <Link
              to='/diligences/$id'
              params={{ id: row.original.diligenceId }}
              className='font-medium text-textPrimary no-underline hover:text-primary'
            >
              {row.original.projectName}
            </Link>
            {row.original.address && (
              <Typography variant='caption' color='text.disabled'>
                {row.original.address}
              </Typography>
            )}
          </div>
        )
      }),
      queueColumns.accessor('since', {
        header: 'Em revisão desde',
        cell: ({ getValue }) => (
          <Typography variant='body2' color='text.secondary'>
            {formatDateTime(getValue())}
          </Typography>
        )
      }),
      queueColumns.accessor('waitingMs', {
        header: 'Esperando há',
        cell: ({ getValue }) => (
          <CustomChip round='true' size='small' variant='tonal' color={waitingColor(getValue())} label={waited(getValue())} />
        )
      }),
      queueColumns.accessor('latestVersion', {
        header: 'Versão do parecer',
        cell: ({ getValue }) => (
          <Typography variant='body2' color='text.secondary'>
            v{getValue()}
          </Typography>
        )
      })
    ],
    []
  )

  const divergenceTableColumns = useMemo(
    () => [
      divergenceColumns.accessor('ruleId', { header: 'Regra' }),
      divergenceColumns.accessor('engineSeverity', {
        header: 'O motor diz',
        cell: ({ getValue }) => (
          <CustomChip
            round='true'
            size='small'
            variant='tonal'
            color={getValue() === 'red' ? 'error' : 'warning'}
            label={getValue() === 'red' ? 'Impeditivo' : 'Atenção'}
          />
        )
      }),
      divergenceColumns.accessor('lowered', { header: 'Advogado rebaixou' }),
      divergenceColumns.accessor('raised', { header: 'Advogado elevou' }),
      divergenceColumns.accessor('cases', { header: 'Casos' })
    ],
    []
  )

  const table = useReactTable({
    data: queue,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: { pagination: { pageSize: 10 } }
  })

  const divergenceTable = useReactTable({
    data: divergence,
    columns: divergenceTableColumns,
    getCoreRowModel: getCoreRowModel()
  })

  return (
    <div className='flex flex-col gap-6'>
      <Card>
        <CardHeader
          title='Fila de revisão'
          subheader='Casos com parecer conclusivo esperando a assinatura de um advogado. Nada sai para o cliente antes dela.'
        />
        <Divider />
        <div className='overflow-x-auto'>
          <TableDefault
            table={table}
            withPagination
            onRowClick={row => navigate({ to: '/diligences/$id', params: { id: row.original.diligenceId } })}
            emptyMessage={isPending ? 'Carregando…' : 'Nenhum caso esperando revisão.'}
          />
        </div>
      </Card>

      {isAdmin && (
        <Card>
          <CardHeader
            title='Divergência entre motor e advogado'
            subheader='Regras cuja gravidade os advogados mais corrigem — o que a matriz precisa rever.'
          />
          <Divider />
          <div className='overflow-x-auto'>
            <TableDefault table={divergenceTable} emptyMessage='Nenhuma gravidade revista até agora.' />
          </div>
        </Card>
      )}
    </div>
  )
}
